import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, Alert, Button, Link } from '@mui/material';
import { useAuth } from '../contexts/GoogleAuthContext';

export default function SignIn() {
  const { signIn, error, loading, googleInitialized } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [buttonRendered, setButtonRendered] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    // Check if Google is loaded
    const checkGoogle = () => {
      if (window.google && window.google.accounts) {
        setGoogleLoaded(true);
      }
    };

    checkGoogle();
    const interval = setInterval(checkGoogle, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Render Google Sign-In button
    console.log('SignIn render attempt:', {
      hasButtonRef: !!buttonRef.current,
      googleLoaded,
      googleInitialized,
      hasGoogle: !!window.google,
      hasRenderButton: !!window.google?.accounts?.id?.renderButton
    });

    if (buttonRef.current && googleLoaded && googleInitialized && window.google) {
      try {
        console.log('Rendering Google button...');
        window.google.accounts.id.renderButton(
          buttonRef.current,
          {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            width: 300,
            logo_alignment: 'left',
            use_fedcm_for_button: true, // Enable FedCM for button
          }
        );
        setButtonRendered(true);

        // Show fallback after 3 seconds if button doesn't appear
        setTimeout(() => {
          const hasIframe = buttonRef.current?.querySelector('iframe');
          const hasDiv = buttonRef.current?.querySelector('div');
          console.log('Button check:', { hasIframe, hasDiv, innerHTML: buttonRef.current?.innerHTML });

          if (buttonRef.current && !hasIframe && !hasDiv) {
            console.log('No button found, showing fallback');
            setShowFallback(true);
          }
        }, 3000);
      } catch (error) {
        console.error('Failed to render Google button:', error);
        setShowFallback(true);
      }
    }
  }, [googleLoaded, googleInitialized]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="600">
            BY.AI Group Command Center
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Sign in with your authorized account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!googleLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Loading Google Sign-In...
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                ref={buttonRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2,
                  minHeight: showFallback ? 0 : 44, // Ensure space for button
                }}
              />

              {showFallback && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Having trouble? Try these options:
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => signIn()}
                    sx={{ mb: 1 }}
                  >
                    Try One Tap Sign-In
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                    If sign-in is blocked, check the icon in your address bar
                  </Typography>
                </Box>
              )}
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            Only authorized @tripsby.ai and @byai.group accounts can access this system
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
